<script setup lang="ts">
import { computed } from 'vue';

type Props = {
  index: number;
  item: any;
  role: string;
  disabled: boolean;
  as: 'li' | 'tr';
  isGrabbed?: boolean;
  instructionsId?: string;
};

const props = withDefaults(defineProps<Props>(), {
  as: 'li',
  isGrabbed: false,
});

const emit = defineEmits<{
  (e: 'click'): void;
  (e: 'toggleGrab'): void;
  (e: 'moveItem', direction: -1 | 1): void;
}>();

// Own class rather than sortablejs's `sortable-chosen`, which sortable adds and strips as it likes.
const extraClasses = computed<string[]>(() => (props.isGrabbed ? ['is-grabbed'] : []));

const onArrow = (direction: -1 | 1) => {
  if (props.disabled || !props.isGrabbed) {
    return;
  }
  emit('moveItem', direction);
};
</script>

<template>
  <component
    :is="as"
    :role="disabled ? null : role"
    :tabindex="disabled ? undefined : 0"
    :aria-describedby="disabled ? undefined : instructionsId"
    :aria-grabbed="disabled ? undefined : isGrabbed"
    class="drag__item"
    :class="[extraClasses]"
    @click="emit('click')"
    @keydown.space.prevent="!disabled && emit('toggleGrab')"
    @keydown.up.prevent="onArrow(-1)"
    @keydown.down.prevent="onArrow(1)"
    @keydown.esc="isGrabbed && emit('toggleGrab')"
  >
    <slot></slot>
  </component>
</template>

<style scoped lang="scss">
/*
  Focus ring for the tab stop (2.4.7), painted by the row as a pseudo-element on top of its own
  content. Every other approach lost: an outline inset behind the row's opaque background, a shadow
  outside it clipped by the list's horizontal overflow, and an inset shadow pushed onto the child
  through `:deep` never matched. An overlay is beholden to none of them.
*/
.drag__item {
  position: relative;
}

.drag__item:focus,
.drag__item:focus-visible {
  outline: none;
}

/*
  `:focus-visible` only, so a click or a tap to place leaves nothing behind. Not while carried
  either: the dashed border already says where the row is, and two rings on one row is one too many.
*/
.drag__item:focus-visible:not(.is-grabbed)::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  border: 2px solid rgb(var(--v-theme-primary));
  border-radius: 4px;
}

.drag__item.is-grabbed {
  background-color: rgba(var(--v-theme-outline), 0.1);
  border: 4px dashed rgb(var(--v-theme-primary));
  cursor: grabbing;
}

/*
  Rendered as an `li`, which is `display: list-item` and paints a ::marker wherever it sits — the
  wrapper's own `ol { list-style: none }` does not reach it when the list tag is a plain div.
*/
.drag__item {
  list-style: none;
}
</style>
